<template>
  <div>
    <div class="flex items-baseline justify-between mb-1">
      <label :for="inputId" class="text-sm font-semibold text-gray-900 dark:text-white">{{ label }}</label>
      <span class="text-xs tabular-nums" :class="atLimit ? 'text-amber-600 dark:text-amber-400' : 'text-gray-400 dark:text-gray-500'">
        {{ items.length }} of {{ max }}
      </span>
    </div>
    <p class="text-xs text-gray-500 dark:text-gray-400 mb-3">{{ description }}</p>

    <!-- Added terms -->
    <ul v-if="items.length" class="flex flex-wrap gap-2 mb-3" :aria-label="label">
      <li v-for="item in items" :key="item.id"
        class="inline-flex items-center gap-1 pl-3 pr-1 py-1 rounded-full text-sm bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300">
        <span>{{ item.name }}</span>
        <button type="button" @click="handleRemove(item)" :disabled="removingId === item.id"
          class="w-5 h-5 flex items-center justify-center rounded-full text-blue-400 hover:text-blue-700 hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors disabled:opacity-50"
          :aria-label="`Remove ${item.name}`">
          <svg class="w-3 h-3" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/>
          </svg>
        </button>
      </li>
    </ul>

    <!-- Add one -->
    <form @submit.prevent="handleAdd" class="flex gap-2">
      <input :id="inputId" v-model="draft" type="text" maxlength="100" :disabled="atLimit || adding"
        :placeholder="atLimit ? `You can add up to ${max}` : placeholder" autocomplete="off"
        class="flex-1 min-w-0 px-3 py-2 text-sm border rounded-lg bg-white dark:bg-gray-800/80 text-gray-900 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/40 disabled:opacity-60 disabled:cursor-not-allowed"
        :class="error ? 'border-red-400 dark:border-red-500' : 'border-gray-200 dark:border-gray-700'"
        @input="error = null">
      <button type="submit" :disabled="!draft.trim() || atLimit || adding"
        class="px-4 py-2 text-sm font-semibold rounded-lg bg-blue-600 hover:bg-blue-700 text-white shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
        {{ adding ? '...' : 'Add' }}
      </button>
    </form>
    <p v-if="error" class="text-xs text-red-500 dark:text-red-400 mt-1.5" role="alert">{{ error }}</p>
    <p v-else-if="!items.length" class="text-xs text-gray-400 dark:text-gray-500 mt-1.5">{{ example }}</p>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'

const props = defineProps({
  label: { type: String, required: true },
  description: { type: String, default: '' },
  placeholder: { type: String, default: '' },
  example: { type: String, default: '' },
  items: { type: Array, required: true },
  max: { type: Number, required: true },
  // Async functions that resolve to { ok, message }.
  add: { type: Function, required: true },
  remove: { type: Function, required: true },
})

const inputId = `term-${Math.random().toString(36).slice(2, 8)}`
const draft = ref('')
const adding = ref(false)
const removingId = ref(null)
const error = ref(null)

const atLimit = computed(() => props.items.length >= props.max)

async function handleAdd() {
  const name = draft.value.trim()
  if (!name || atLimit.value) return

  adding.value = true
  const result = await props.add(name)
  adding.value = false

  if (result.ok) draft.value = ''
  else error.value = result.message
}

async function handleRemove(item) {
  removingId.value = item.id
  const result = await props.remove(item.id)
  removingId.value = null
  if (!result.ok) error.value = result.message
}
</script>
